import type { MarkdownIt } from 'markdown-it'
import type { Source } from '@/services/types'

export const citationIdCharacterClass = '[^\\]\\s]+'
export const inlineCitationRegex = /\[\^([^\]\s]+)\]/g
// Matches a run of one or more inline citations separated only by horizontal whitespace,
// so adjacent groups like [^a][^b] or [^a] [^b] are processed together for dedup + sorting.
export const citationGroupRegex = /\[\^[^\]\s]+\](?:[ \t]*\[\^[^\]\s]+\])*/g
const footnoteDefinitionRegex = /^\[\^([^\]\s]+)\]:\s*([\s\S]*?)(?=(?:\n\[\^[^\]\s]+\]:)|$)/gm

export interface MarkdownCitation {
  id: string
  number: number
  title: string
  link?: string
  anchor: string
}

interface CitationContext {
  provider: CitationProviderImpl
}

interface CitationEnv {
  citationContext?: CitationContext
}

interface CitationProviderOptions {
  sources?: Source[]
  footnotePrefix?: string
  escapeHtml: (value: string) => string
}

export class CitationProviderImpl {
  private sources = new Map<string, Source>()
  private order: string[] = []
  private idAliases = new Map<string, string>()
  private readonly footnotePrefix: string
  private readonly escapeHtml: (value: string) => string

  constructor(options: CitationProviderOptions) {
    this.footnotePrefix = options.footnotePrefix ?? 'source'
    this.escapeHtml = options.escapeHtml
    this.addSources(options.sources ?? [])
  }

  addSources(sources: Source[]) {
    sources.forEach(source => {
      if (source?.id) {
        const normalizedSourceId = source.id
        const canonicalId = this.getCanonicalId(source)
        const existingAlias = this.idAliases.get(normalizedSourceId)
        const existingSource = existingAlias ? this.sources.get(existingAlias) : undefined
        if (existingAlias && existingSource) {
          const incomingScore = this.getSourceScore(source)
          const existingScore = this.getSourceScore(existingSource)
          if (existingScore > incomingScore) {
            const merged: Source = {
              ...existingSource,
              title: existingSource.title || source.title,
              link: existingSource.link || source.link,
            }
            this.sources.set(existingAlias, merged)
            return
          }
        }
        this.idAliases.set(normalizedSourceId, canonicalId)
        const existing = this.sources.get(canonicalId)
        if (!existing) {
          this.sources.set(canonicalId, source)
          return
        }
        const merged: Source = {
          ...existing,
          title: existing.title || source.title,
          link: existing.link || source.link,
        }
        this.sources.set(canonicalId, merged)
      }
    })
  }

  replaceInline(content: string) {
    citationGroupRegex.lastIndex = 0
    return content.replace(citationGroupRegex, group => this.renderGroup(group, true))
  }

  renderCitationContent(content: string) {
    const titleWithoutLeakedSourceTag = content.replace(/<\/src>\s*$/i, '')
    const fragments: string[] = []
    let lastIndex = 0
    for (const m of titleWithoutLeakedSourceTag.matchAll(citationGroupRegex)) {
      const offset = m.index!
      fragments.push(this.escapeHtml(titleWithoutLeakedSourceTag.slice(lastIndex, offset)))
      fragments.push(this.renderGroup(m[0], false))
      lastIndex = offset + m[0].length
    }
    fragments.push(this.escapeHtml(titleWithoutLeakedSourceTag.slice(lastIndex)))
    return fragments.join('')
  }

  getCitations(): MarkdownCitation[] {
    return this.order.map((id, index) => {
      const canonicalId = this.resolveId(id)
      const source = this.sources.get(canonicalId)
      const number = index + 1
      if (!source) {
        return {
          id,
          number,
          title: '',
          anchor: `citation-${this.footnotePrefix}-${number}`,
        }
      }
      const title = this.renderCitationContent(source.title ?? '')
      return {
        id: canonicalId,
        number,
        title,
        link: source.link,
        anchor: `citation-${this.footnotePrefix}-${number}`,
      }
    })
  }

  // Renders a group of consecutive citations (e.g. "[^a][^b]" or "[^a] [^b]"):
  // • deduplicates by canonical source ID within the group
  // • sorts the rendered superscripts in ascending citation-number order
  // Citations with no matching source are silently dropped.
  private renderGroup(group: string, allowAssign: boolean): string {
    const seen = new Set<string>()
    const entries: Array<{ number: number; html: string }> = []

    for (const m of group.matchAll(inlineCitationRegex)) {
      const id = m[1]!
      const canonicalId = this.resolveId(id)
      const source = this.sources.get(canonicalId)

      if (!source) continue
      if (seen.has(canonicalId)) continue
      seen.add(canonicalId)

      let position = this.order.indexOf(canonicalId)
      if (position === -1) {
        if (!allowAssign) continue
        this.order.push(canonicalId)
        position = this.order.length - 1
      }
      const number = position + 1
      entries.push({ number, html: this.renderSupElement(number) })
    }

    entries.sort((a, b) => a.number - b.number)
    return entries.map(e => e.html).join('')
  }

  private renderSupElement(number: number): string {
    return `<sup class="citation"><a href="#citation-${this.escapeHtml(this.footnotePrefix)}-${number}" data-footnote-link tabindex="-1">[${number}]</a></sup>`
  }

  private getCanonicalId(source: Source) {
    const linkKey = source.link?.trim()
    const titleKey = source.title?.trim()
    if (linkKey && titleKey) {
      return `${linkKey}::${titleKey}`
    }
    return source.id
  }

  private getSourceScore(source: Source) {
    const hasLink = Boolean(source.link?.trim())
    const hasTitle = Boolean(source.title?.trim())
    return (hasLink ? 2 : 0) + (hasTitle ? 1 : 0)
  }

  private resolveId(id: string) {
    const normalizedId = id
    return this.idAliases.get(normalizedId) ?? normalizedId
  }
}

export function createCitationPlugin() {
  return (md: MarkdownIt) => {
    md.core.ruler.before('normalize', 'citation_extract', state => {
      const env = state.env as CitationEnv
      const ctx = env.citationContext
      if (!ctx) return
      const provider = ctx.provider
      const { text, footnotes } = extractFootnotes(state.src)
      provider.addSources(footnotes)
      state.src = text
    })

    const defaultTextRule = md.renderer.rules.text

    md.renderer.rules.text = (tokens, idx, options, env, self) => {
      const token = tokens[idx]
      if (!token) return ''
      const envCtx = (env as CitationEnv).citationContext
      if (!envCtx) {
        if (defaultTextRule) {
          return defaultTextRule(tokens, idx, options, env, self)
        }
        return token.content ?? ''
      }
      const content = token.content ?? ''
      if (!citationGroupRegex.test(content)) {
        if (defaultTextRule) {
          return defaultTextRule(tokens, idx, options, env, self)
        }
        return content
      }
      // replaceInline resets citationGroupRegex.lastIndex before use
      return envCtx.provider.replaceInline(content)
    }
  }
}

function extractFootnotes(src: string) {
  const footnotes: Source[] = []
  const text = src.replace(footnoteDefinitionRegex, (_match, id: string, content: string) => {
    footnotes.push({
      id: id,
      title: content.trim(),
    })
    return ''
  })

  return { text, footnotes }
}
